import { useCallback, useEffect, useState } from 'react'
import { outboxApi, type FailedOutboxRow, type FailedOutboxRowDetail } from '../api/outbox'
import {
  Page,
  PageHeader,
  Button,
  AccessDenied,
  Callout,
  LoadingState,
  EmptyState,
  Card,
  DataTable,
  Dialog,
  Pagination,
  KeyValueList,
} from '../components/ui'

const PAGE_SIZE = 25

function formatDateTime(s: string): string {
  return new Date(s).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function truncate(s: string | null, n: number): string {
  if (!s) return '—'
  return s.length > n ? `${s.slice(0, n)}…` : s
}

// TASK-0098, same detection mechanism TASK-0088 established on PaymentsPage.tsx/ReportsPage.tsx:
// `api()` (client.ts) throws a plain Error whose message is either the backend's JSON `title`/
// `message`, or (for a body-less response like the 403 the [Authorize] middleware returns here)
// the fetch `Response.statusText`, which Kestrel sets to the standard HTTP reason phrase
// ("Forbidden"). Checking both the digits and the reason phrase covers either shape.
function isForbidden(e: unknown): boolean {
  const msg = e instanceof Error ? e.message : ''
  return msg.includes('403') || msg.toLowerCase().includes('forbidden') || msg.toLowerCase().includes('autorizzat')
}

// The 404 branch of GetFailed/Replay returns no body (see OutboxController.cs), so the thrown
// Error's message is the fetch statusText ("Not Found") rather than anything Italian — detect it
// the same way isForbidden() detects a body-less 403, then substitute a real sentence for it.
function isNotFound(e: unknown): boolean {
  const msg = e instanceof Error ? e.message : ''
  return msg.includes('404') || msg.toLowerCase().includes('not found')
}

export function OutboxPage() {
  const [items, setItems] = useState<FailedOutboxRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [unauthorized, setUnauthorized] = useState(false)

  // Detail view (payload) — dialog (<Dialog> of components/ui).
  const [detailRow, setDetailRow] = useState<FailedOutboxRowDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState<string | null>(null)
  const [detailForId, setDetailForId] = useState<number | null>(null)

  // Replay confirmation — a second modal, separate from the detail one, so "Riprova" from the
  // table never fires the request on the triggering click: it only opens this dialog. The
  // in-flight guard (`replaying`) then disables the dialog's own confirm button so a double click
  // on *that* button can't submit twice either.
  const [replayRow, setReplayRow] = useState<FailedOutboxRow | null>(null)
  const [replaying, setReplaying] = useState(false)
  const [replayError, setReplayError] = useState<string | null>(null)

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    setUnauthorized(false)
    try {
      const result = await outboxApi.listFailed(page, PAGE_SIZE)
      setItems(result.items)
      setTotal(result.total)
    } catch (e) {
      if (isForbidden(e)) {
        setUnauthorized(true)
      } else {
        setError(e instanceof Error ? e.message : 'Errore')
      }
    } finally {
      setLoading(false)
    }
  }, [page])

  useEffect(() => {
    void load()
  }, [load])

  async function openDetail(row: FailedOutboxRow): Promise<void> {
    setDetailForId(row.id)
    setDetailRow(null)
    setDetailError(null)
    setDetailLoading(true)
    try {
      const full = await outboxApi.getFailed(row.id)
      setDetailRow(full)
    } catch (e) {
      if (isForbidden(e)) {
        setDetailForId(null)
        setUnauthorized(true)
      } else if (isNotFound(e)) {
        setDetailError('Questa riga non è più disponibile: probabilmente è già stata gestita o rimessa in coda da un altro operatore.')
      } else {
        setDetailError(e instanceof Error ? e.message : 'Errore')
      }
    } finally {
      setDetailLoading(false)
    }
  }

  function closeDetail(): void {
    setDetailForId(null)
    setDetailRow(null)
    setDetailError(null)
  }

  function openReplayConfirm(row: FailedOutboxRow): void {
    setReplayError(null)
    setReplayRow(row)
  }

  function closeReplayConfirm(): void {
    if (replaying) return // in-flight: ignore stray dismiss clicks until the request settles
    setReplayRow(null)
    setReplayError(null)
  }

  async function confirmReplay(): Promise<void> {
    if (!replayRow || replaying) return
    const row = replayRow
    setReplaying(true)
    setReplayError(null)
    try {
      await outboxApi.replay(row.id)
      // Success (204): the row is no longer Failed, so drop it from the local list instead of a
      // full reload — cheaper, and it preserves the operator's current page/scroll position. If
      // that emptied the current (non-first) page, step back a page so they land somewhere with
      // rows rather than on a page that now renders empty.
      setItems(prev => {
        const next = prev.filter(r => r.id !== row.id)
        if (next.length === 0 && page > 1) {
          setPage(p => p - 1)
        }
        return next
      })
      setTotal(t => Math.max(0, t - 1))
      setReplayRow(null)
    } catch (e) {
      if (isForbidden(e)) {
        setReplayRow(null)
        setUnauthorized(true)
      } else if (isNotFound(e)) {
        setReplayError('La riga non esiste più o è già stata gestita da un altro operatore. Aggiorna la lista.')
      } else {
        // Covers 409: the backend already sends a human-readable Italian message for it
        // ("La riga outbox è cambiata mentre veniva rimessa in coda; rileggila e riprova."),
        // which api() surfaces verbatim as e.message — no further translation needed here.
        setReplayError(e instanceof Error ? e.message : 'Errore durante il replay')
      }
    } finally {
      setReplaying(false)
    }
  }

  return (
    <Page>
      <PageHeader
        title="Outbox"
        icon="unarchive"
        subtitle="Righe fallite"
        count={!unauthorized ? total : undefined}
      />

      {unauthorized && <AccessDenied requirement="il ruolo Admin" />}

      {!unauthorized && error && <Callout tone="error">{error}</Callout>}

      {!unauthorized && (
        <Card variant="outlined" padding="none">
          {loading ? (
            <LoadingState />
          ) : items.length === 0 ? (
            <EmptyState icon="task_alt" title="Nessuna riga outbox in stato Failed" />
          ) : (
            <DataTable variant="plain">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Evento</th>
                  <th>Tentativi</th>
                  <th>Ultimo errore</th>
                  <th>Avvenuto il</th>
                  <th className="text-end">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {items.map(row => (
                  <tr key={row.id}>
                    <td className="text-muted md-typescale-body-small md-num">{row.id}</td>
                    <td className="md-typescale-emphasized-body-medium">{row.eventType}</td>
                    <td className={`md-num ${row.attempts > 0 ? 'text-warning md-typescale-emphasized-body-medium' : 'text-muted'}`}>{row.attempts}</td>
                    <td className="text-muted md-typescale-body-small">{truncate(row.lastError, 80)}</td>
                    <td className="text-muted md-typescale-body-small text-nowrap">{formatDateTime(row.occurredAt)}</td>
                    <td className="text-end">
                      <div className="d-flex gap-2 justify-content-end">
                        <Button variant="outlined" size="sm" onClick={() => void openDetail(row)}>
                          Dettagli
                        </Button>
                        <Button variant="tonal" size="sm" onClick={() => openReplayConfirm(row)}>
                          Riprova
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          )}
          {totalPages > 1 && <Pagination page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} />}
        </Card>
      )}

      {/* Detail dialog — full payload for one row, fetched from GET /api/outbox/failed/{id}. */}
      <Dialog
        open={detailForId !== null}
        onClose={closeDetail}
        title={`Riga outbox #${detailForId ?? ''}`}
        actions={<Button variant="text" dismiss onClick={closeDetail}>Chiudi</Button>}
      >
        {detailLoading ? (
          <LoadingState />
        ) : detailError ? (
          <Callout tone="warning">{detailError}</Callout>
        ) : detailRow ? (
          <>
            <KeyValueList
              items={[
                { label: 'Evento', value: detailRow.eventType },
                { label: 'Tentativi', value: detailRow.attempts },
                { label: 'Ultimo errore', value: detailRow.lastError ?? '—' },
                { label: 'Avvenuto il', value: formatDateTime(detailRow.occurredAt) },
                { label: 'Creato il', value: formatDateTime(detailRow.createdAt) },
              ]}
            />
            <div className="d-flex flex-column gap-1">
              <div className="md-typescale-label-medium text-muted">Payload</div>
              <pre className="md-typescale-body-small mb-0 p-2 rounded md-u-scroll-y-320 md-u-pre-wrap md-u-break-word md-u-code-box">
                {detailRow.payload}
              </pre>
            </div>
          </>
        ) : null}
      </Dialog>

      {/* Replay confirmation dialog — the required explicit confirmation step: "Riprova" in the
          table only opens this dialog, it never sends the request by itself. */}
      <Dialog
        open={replayRow !== null}
        onClose={closeReplayConfirm}
        dismissible={!replaying}
        title="Confermare il replay?"
        supportingText={replayRow ? (
          <>
            Stai per rimettere in coda la riga outbox <strong>#{replayRow.id}</strong> (
            <code>{replayRow.eventType}</code>) come se non fosse mai stata elaborata. L'evento
            verrà consegnato di nuovo ai suoi consumer.
          </>
        ) : undefined}
        actions={
          <>
            <Button variant="text" onClick={closeReplayConfirm} disabled={replaying}>
              Annulla
            </Button>
            <Button variant="text" disabled={replaying} onClick={() => void confirmReplay()}>
              {replaying ? 'Invio...' : 'Conferma replay'}
            </Button>
          </>
        }
      >
        {replayRow && replayError && <Callout tone="warning">{replayError}</Callout>}
      </Dialog>
    </Page>
  )
}
